import { useState } from "react";
import { Link } from "react-router-dom";
import { Panel } from "@/components/app/common";
import { useFetch } from "@/hooks/useFetch";
import { inr0 as inr } from "@/lib/format";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (k) => (/^\d{4}-\d{2}$/.test(k) ? `${MON[Number(k.slice(5)) - 1]} ${k.slice(0, 4)}` : k);
const cr = (v) => inr(v);

// One row: label, bar split into confirmed (sky) and projection (violet), values.
function BarRow({ b, max, name, tone = "", testid, to }) {
  const tot = b.confirmed + b.projection;
  return (
    <Link to={to} className="grid grid-cols-[minmax(0,10rem)_1fr_auto] items-center gap-3 py-1.5 px-1 -mx-1 rounded border-b border-slate-100 last:border-0 text-sm hover:bg-sky-50 transition-colors" title={`Open these LMs in the LM Register`} data-testid={testid}>
      <div className={`truncate ${tone}`} title={name}>{name}</div>
      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden flex">
        <div className="bg-sky-600" style={{ width: `${(b.confirmed / max) * 100}%` }} />
        <div className="bg-violet-400" style={{ width: `${(b.projection / max) * 100}%` }} />
      </div>
      <div className="text-right font-mono text-xs whitespace-nowrap">
        {inr(tot)} <span className="text-slate-400">· {b.lms} LM{b.lms === 1 ? "" : "s"}</span>
        {b.projection > 0 && <div className="text-[10px] text-violet-700">incl. projection {inr(b.projection)}</div>}
      </div>
    </Link>
  );
}

function Breakdown({ title, rows, testid, nameOf = (b) => b.key, toneOf, limit, linkOf }) {
  const [all, setAll] = useState(false);
  const max = Math.max(1, ...rows.map((b) => b.confirmed + b.projection));
  const shown = limit && !all ? rows.slice(0, limit) : rows;
  return (
    <Panel title={title} testid={testid}
      actions={limit && rows.length > limit && <button type="button" className="text-xs text-sky-700 hover:underline" onClick={() => setAll(!all)} data-testid={`${testid}-toggle`}>{all ? "Show top " + limit : `Show all ${rows.length}`}</button>}>
      {shown.map((b) => <BarRow key={b.key} b={b} max={max} name={nameOf(b)} tone={toneOf?.(b)} testid={`${testid}-row`} to={`/orders?${new URLSearchParams({ from: `${title}: ${nameOf(b)}`, ...linkOf(b) })}`} />)}
      {!rows.length && <div className="text-xs text-slate-400">No open LMs</div>}
    </Panel>
  );
}

export function BusinessPipeline() {
  const { data } = useFetch("/dashboard/pipeline");
  if (!data) return null;
  const t = data.totals;
  const past = (b) => /^\d{4}-\d{2}$/.test(b.key) && b.key < data.this_month;
  return (
    <div className="mt-6" data-testid="business-pipeline">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-slate-900">Pending order pipeline</h2>
          <p className="text-xs text-slate-500">All values are <b>basic values, excluding GST</b>. Open LMs (pending approval → partially dispatched), value still to dispatch; projections at their unbooked balance. Click any row to open those LMs.</p>
        </div>
        <div className="flex gap-4 text-xs" data-testid="pipeline-totals">
          <span><span className="inline-block h-2 w-2 rounded-full bg-sky-600 mr-1" />Confirmed <b className="font-mono">{cr(t.confirmed)}</b></span>
          <span><span className="inline-block h-2 w-2 rounded-full bg-violet-400 mr-1" />Projection <b className="font-mono">{cr(t.projection)}</b></span>
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Breakdown title="Monthly invoicing plan" rows={data.months} testid="pipeline-months" nameOf={(b) => `${monthLabel(b.key)}${past(b) ? " (overdue)" : ""}`}
          toneOf={(b) => (past(b) ? "text-amber-700" : b.key === "Unplanned" ? "text-slate-400" : "")} linkOf={(b) => ({ invoice_month: b.key })} />
        <Breakdown title="Customer-wise" rows={data.customers} testid="pipeline-customers" limit={12} linkOf={(b) => ({ customer_id: b.customer_id })} />
        <Breakdown title="Product category-wise" rows={data.categories} testid="pipeline-categories" linkOf={(b) => ({ product_category: b.key })} />
      </div>
    </div>
  );
}
